import {debounce} from 'throttle-debounce';

type Options = Partial<SearchBoxOptions>;

export interface SearchBoxElement extends HTMLElement {
  searchbox?: SearchBox;
}

export interface SearchBoxOptions {
  activeClass: string;
  boostOverlay: string;
  contentEl: string;
  debounce: number;
  fieldEl: string;
  selector: string;
  triggerEl: string;
}

export class SearchBox {
  static defaults: SearchBoxOptions = {
    activeClass: 'is-active',
    boostOverlay: '.boost-pfs-search-suggestion-mobile-overlay',
    contentEl: '[data-searchbox-content]',
    debounce: 300,
    fieldEl: '[data-searchbox-input]',
    selector: '[data-searchbox]',
    triggerEl: '[data-searchbox-trigger]',
  };
  element: SearchBoxElement;
  options: SearchBoxOptions;
  content: HTMLElement;
  field: HTMLInputElement;
  focusHandler: EventListener;
  blurHandler: EventListener;
  keyupHandler: EventListener;
  clickHandler: EventListener;

  constructor(element: SearchBoxElement, options: Options = {}) {
    if (!element || element.searchbox) {
      return;
    }

    element.searchbox = this;
    this.element = element;
    this.options = {
      ...SearchBox.defaults,
      ...this.element.dataset,
      ...options,
    };

    const content = this.element.querySelector(
      this.options.contentEl,
    ) as HTMLElement;

    const field = this.element.querySelector(
      this.options.fieldEl,
    ) as HTMLInputElement;

    if (!content || !field) {
      return;
    }

    this.content = content;
    this.field = field;

    this.bind();
  }

  bind() {
    this.focusHandler = this.handleFocus.bind(this);
    this.blurHandler = this.handleBlur.bind(this);
    this.clickHandler = this.handleTriggerClick.bind(this);
    this.keyupHandler = debounce(
      this.options.debounce,
      this.handleKeyUp.bind(this),
    );

    this.element.addEventListener('focus', this.focusHandler, true);
    this.element.addEventListener('blur', this.blurHandler, true);
    this.element.addEventListener('click', this.clickHandler);
    this.element.addEventListener('keyup', this.keyupHandler);
    document.addEventListener(
      'sticky-header:active',
      this.handleStickyHeaderChange.bind(this) as EventListener,
    );
    document.addEventListener(
      'sticky-header:inactive',
      this.handleStickyHeaderChange.bind(this) as EventListener,
    );
  }

  handleKeyUp() {
    const event = new Event('searchkeyup', {
      bubbles: true,
    });

    this.element.dispatchEvent(event);

    this.checkSearchOpen();
  }

  handleFocus(event: Event) {
    this.toggleField(event.target as HTMLElement, true);
  }

  handleBlur(event: Event) {
    this.toggleField(event.target as HTMLElement, false);
  }

  handleTriggerClick(event: Event) {
    const target = event.target as HTMLElement;

    if (!target.closest(this.options.triggerEl)) {
      return;
    }

    event.preventDefault();
    this.content.classList.toggle(this.options.activeClass);
    this.field.focus();
  }

  handleStickyHeaderChange() {
    if (this.element.contains(document.activeElement)) {
      return;
    }

    this.content.classList.remove(this.options.activeClass);
  }

  toggleField(element: HTMLElement, state: boolean) {
    const match = element.closest(this.options.fieldEl);

    if (!match) {
      return;
    }

    this.element.classList.toggle(this.options.activeClass, state);
  }

  checkSearchOpen() {
    // ensure boost search input is open on mobile (previously would not open if user clicked native search input too early)
    const boostOverlay = document.querySelector(
      this.options.boostOverlay,
    ) as HTMLDivElement;
    if (boostOverlay && boostOverlay.style.display !== 'block') {
      this.content.classList.toggle(this.options.activeClass);
      this.field.blur();
      this.field.focus();
    }
  }

  destroy() {
    this.element.removeEventListener('focus', this.focusHandler, true);
    this.element.removeEventListener('blur', this.blurHandler, true);
    this.element.removeEventListener('click', this.clickHandler);
    this.element.removeEventListener('keyup', this.keyupHandler);
    document.removeEventListener('sticky-header:active', this
      .handleStickyHeaderChange as EventListener);
    document.removeEventListener('sticky-header:inactive', this
      .handleStickyHeaderChange as EventListener);

    this.element.searchbox = undefined;
  }
}
